import {List, Record, Map, Set} from 'immutable';
import type {ItemRecordType as ItemType} from '@packages/systems/core/records/ItemRecord';
import type {LocaleItemContext} from '@packages/systems/plugins/Localization';
import type {
  Locale,
  LocaleTag,
  LocaleSubdirectory,
  LocalizationStagedState,
  LocalizationSettings,
  LocalizedPageCanonicalUrlsByLocaleId,
} from '@packages/systems/localization-types';
import {
  RTL_LANG_CODES,
  LOCALIZATION_SETTINGS_FORM_ID,
  ISO_LANGUAGES,
  ISO_COUNTRIES,
} from '@packages/systems/localization-constants';
import type {
  ItemCountByCollectionIdType,
  LocalizedItemCountByLocaleType,
} from '@packages/systems/core/records/CollectionStateType';
import type {GetCollectionsResponse} from '@packages/systems/cms/types';

export const localeTagRegex = new RegExp(
  `^(${Object.keys(ISO_LANGUAGES).join('|')})(-(${Object.keys(ISO_COUNTRIES).join('|')}))?$`
);

export default class LocaleUtils {
  /**
   * Returns the language
   * description of a tag.
   */
  static getTagDescription(localeTag: string): null | string {
    const [isoLang, isoCountry] = localeTag.split('-');
    const lang = ISO_LANGUAGES[isoLang as keyof typeof ISO_LANGUAGES] ?? '';

    if (!lang) {
      return null;
    }

    const country =
      ISO_COUNTRIES[isoCountry as keyof typeof ISO_COUNTRIES] ?? '';

    return `${lang}${country ? ` (${country})` : ''}`;
  }

  /**
   * Returns true if the full language tag is supported.
   */
  static validateTag(localeTag: Locale['tag']): boolean {
    return localeTagRegex.test(localeTag);
  }

  /**
   * Returns true if the subdirectory is a valid URL slug
   */
  static validateSubdirectory(subdirectory: Locale['subdirectory']): boolean {
    return Boolean(subdirectory && /^[\w-]{1,16}$/.test(subdirectory));
  }

  /**
   * Returns the subdirectory of a locale to be used for example
   * in a process of constructing a full href of a page.
   */
  static getSubdirectory(
    locale: Locale | null | undefined
  ): LocaleSubdirectory {
    if (!locale) {
      return '';
    }
    if (locale.primary && !locale.enabled) {
      return '';
    }
    return (locale.subdirectory || locale.tag).toLowerCase();
  }

  /**
   * Returns the display name or tag.
   */
  static getDisplayName(locale: Locale): string {
    return (
      locale.displayName || this.getTagDescription(locale.tag) || locale.tag
    );
  }

  private static buildLocaleItemContext(
    locale: Locale,
    {
      current,
      pageId,
      pageHrefByLocaleId,
      primaryPageCanonicalUrl,
      localizedSettings,
      localizedPageCanonicalUrlsByLocaleId,
      isAnyLocaleDrafted,
    }: {
      current?: LocaleTag | null | undefined;
      pageId?: string | null;
      pageHrefByLocaleId?: {[localeId: string]: string} | null;
      primaryPageCanonicalUrl?: string | null | undefined;
      localizedSettings?: LocalizationSettings | null;
      localizedPageCanonicalUrlsByLocaleId?: LocalizedPageCanonicalUrlsByLocaleId;
      isAnyLocaleDrafted: boolean;
    }
  ): LocaleItemContext {
    const draft = localizedSettings
      ? (localizedSettings[locale._id]?.draft ?? isAnyLocaleDrafted)
      : false;

    const [languageCode, countryCode] = locale.tag.split('-');

    return {
      _id: locale._id,
      tag: locale.tag,
      languageCode: languageCode ?? '',
      countryCode: countryCode ?? '',
      subdirectory: LocaleUtils.getSubdirectory(locale),
      displayName: LocaleUtils.getDisplayName(locale),
      displayImageId: locale.displayImageId,
      active: locale.tag === current,
      primary: Boolean(locale.primary),
      cmsId: String(locale.cmsId),
      // Publish-time renderData carries `page._id` as a Mongoose ObjectId (only
      // the S3 JSON round-trip turns it into a string), and the Link atom reads
      // this back through `getString`, which rejects non-strings. Without the
      // coercion the statically compiled 404/401 HTML renders every locale link
      // as href="#".
      pageId: pageId != null ? String(pageId) : pageId,
      // When the page is in draft status, we don't want
      // to link to the page anymore within the Locales List component,
      // since that would direct the user to a 404.
      // so in this case we prefer to link to the subdirectory when
      // the current page is in draft status.
      pageHref: draft
        ? `/${LocaleUtils.getSubdirectory(locale)}`
        : pageHrefByLocaleId?.[String(locale._id)],
      canonicalUrl: locale.primary
        ? primaryPageCanonicalUrl
        : localizedPageCanonicalUrlsByLocaleId?.[locale._id],
      draft,
    };
  }

  /**
   * Returns an object for the Localization plugin
   * context
   */
  static getPluginContext({
    locales,
    current,
    pageId,
    pageHrefByLocaleId,
    primaryPageCanonicalUrl,
    localizedSettings,
    localizedPageCanonicalUrlsByLocaleId,
  }: {
    locales?: Array<Locale> | null | undefined;
    current?: LocaleTag | null | undefined;
    pageId?: string | null;
    pageHrefByLocaleId?: {[localeId: string]: string} | null;
    primaryPageCanonicalUrl?: string | null | undefined;
    localizedSettings?: LocalizationSettings | null;
    localizedPageCanonicalUrlsByLocaleId?: LocalizedPageCanonicalUrlsByLocaleId;
  }): Map<string, Array<LocaleItemContext> | LocaleItemContext | undefined> {
    if (!locales) {
      return Map();
    }

    const isAnyLocaleDrafted = Object.values(localizedSettings ?? {}).some(
      ({draft} = {}) => draft
    );
    const currentLocaleData = locales.find((locale) => current === locale.tag);

    const buildOptions = {
      current,
      pageId,
      pageHrefByLocaleId,
      primaryPageCanonicalUrl,
      localizedSettings,
      localizedPageCanonicalUrlsByLocaleId,
      isAnyLocaleDrafted,
    };

    const localeItems = locales
      .filter((locale) => locale.enabled || locale.primary)
      .map((locale) => this.buildLocaleItemContext(locale, buildOptions));

    const currentLocaleItem = currentLocaleData
      ? this.buildLocaleItemContext(currentLocaleData, buildOptions)
      : undefined;

    return Map({
      currentLocale: currentLocaleItem,
      locales: localeItems,
    });
  }

  /**
   * Checks if a locale tag's content should be rendered RTL
   */
  static isRTL(localeTag?: LocaleTag | null): boolean {
    if (!localeTag) {
      return false;
    }

    /**
     * Partial match locale tags with regions to just the language code
     */
    const localeCode = localeTag.includes('-')
      ? localeTag.split('-')[0]
      : localeTag;

    if (localeCode && RTL_LANG_CODES.has(localeCode)) {
      return true;
    }

    return false;
  }
}

export const makeLocale: Record.Factory<Locale> = Record<Locale>({
  // @ts-expect-error - error TS2322: Type 'undefined' is not assignable to type 'string'.
  _id: undefined,
  cmsId: undefined,
  redirect: true,
  enabled: false,
  subdirectory: undefined,
  displayName: '',
  displayImageId: undefined,
  tag: '',
  primary: false,
  preventPrimaryCmsItemPropagation: false,
});

export const makeStagedState: Record.Factory<LocalizationStagedState> =
  Record<LocalizationStagedState>({
    _id: LOCALIZATION_SETTINGS_FORM_ID,
    dirty: false,
    isSaving: false,
    locales: List([]),
    preStagedLocales: List([]),
    primaryLocale: makeLocale({primary: true}),
    selectedLocaleIndex: undefined,
    publishedSiteBrowserDetection: false,
    enableHreflangLinks: true,
    hrefPrefixForHreflangLinks: '',
    hrefPrefixSameAsCanonicalUrl: true,
    createLanguageTargetTags: Set<string>(),
  });

// Returns all items in the collection that are associated with the given item
// Does not return the item specified in its params by default, but will return it if specificed
export const getAllAssociatedItemsInLocale = (
  item: ItemType,
  itemsInCollection: List<ItemType>,
  filterOutItemLocale: boolean = true
): List<ItemType> => {
  const itemLocale = item.get('_locale');
  return itemsInCollection
    .filter((itemInCollection) => {
      const locale = itemInCollection?.get('_locale');
      const filterLocale = filterOutItemLocale ? locale !== itemLocale : true;
      return (
        locale != null &&
        item.get('_id') === itemInCollection?.get('_id') &&
        filterLocale
      );
    })
    .toList();
};

/**
 * Transforms the collection response to create the localized
 * item count by locale id shape
 */
export function transformCollectionsResponseToLocalizedItemCountByLocaleId(
  jsonCollections: GetCollectionsResponse
): LocalizedItemCountByLocaleType {
  return jsonCollections
    .reduce((localizedItemCountByLocaleId, collection) => {
      const {totalNumberOfItemsByLocaleId} = collection;
      if (!totalNumberOfItemsByLocaleId) {
        return localizedItemCountByLocaleId;
      }

      Object.entries(totalNumberOfItemsByLocaleId).forEach(
        ([localeId, itemCount]) => {
          localizedItemCountByLocaleId.setIn(
            [localeId, collection._id],
            itemCount
          );
        }
      );

      return localizedItemCountByLocaleId;
    }, Map<string, ItemCountByCollectionIdType>().asMutable())
    .asImmutable();
}

/**
 * Merges two localized item count by locale id objects into the same one
 * incoming values take priority over existing ones
 */
export function mergeLocalizedItemCountByLocaleId(
  base: LocalizedItemCountByLocaleType,
  incoming: LocalizedItemCountByLocaleType
): LocalizedItemCountByLocaleType {
  const nextLocalizedItemCountByLocaleId = base.asMutable();

  incoming.forEach((itemCountByLocaleId, localeId) => {
    itemCountByLocaleId?.forEach((itemCount, collectionId) => {
      nextLocalizedItemCountByLocaleId.setIn(
        [localeId, collectionId],
        itemCount
      );
    });
  });

  return nextLocalizedItemCountByLocaleId.asImmutable();
}
